import React, { useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import axios from "axios";
import { Trans, useTranslation } from "react-i18next";
import { useToastPromise } from "@/hooks/useToast";
import { error, warning } from "@/utils/toast";

import {
  Button,
  chakra,
  Flex,
  FormControl,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { dataURLtoFileList, imageSize, ImageSize } from "@/utils/helpers";

const EXTRACT_FILE_NAME_REGEX = /.*[\/\\]/;

const AvatarEdit = dynamic(() => import("react-avatar-edit"), {
  ssr: false,
});

const ChooseAvatarModal = ({
  name,
  url,
  onClose,
  onChange,
}:
  | {
      name: string;
      url: string;
      onClose: () => void;
      onChange: () => void;
    }
  | {
      name?: never;
      url?: never;
      onClose: () => void;
      onChange: (file: File) => void;
    }): JSX.Element => {
  const { t } = useTranslation("common");
  const fileInput = useRef<HTMLInputElement>();
  const [selectedPicture, setSelectedPicture] =
    useState<{ file: string; name: string }>(null);
  const [selectedPictureDimensions, setSelectedPictureDimensions] =
    useState<ImageSize>(null);
  const [croppedPicture, setCroppedPicture] = useState<string>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toastPromise = useToastPromise();
  const toast = useToast();

  const uploadPicture = () => {
    if (!selectedPicture) {
      toast(error(t("chooseFile")));
    }

    const fileList = dataURLtoFileList(
      croppedPicture,
      selectedPicture.name.replace(EXTRACT_FILE_NAME_REGEX, "")
    )[0];

    if (fileInput?.current?.value) fileInput.current.value = null;

    if (!url) {
      onChange(fileList);
      onClose();

      return;
    }

    const formData = new FormData();
    formData.append(name, fileList);

    setIsSubmitting(true);

    toastPromise.promise(
      axios
        .post(url, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then(() => {
          onChange(undefined);
          onClose();
        })
        .finally(() => setIsSubmitting(false)),
      {}
    );
  };

  useEffect(() => {
    if (fileInput?.current) fileInput.current.value = "";
  }, []);

  const onDrag = (e) => {
    e.preventDefault();
  };

  const hasRightMime = (file: File) =>
    file.type === "image/jpeg" || file.type === "image/png";

  const onFileDrop = async (e) => {
    e.preventDefault();
    const { files } = e.dataTransfer;

    if (files.length > 0) {
      if (hasRightMime(files[0])) {
        setSelectedPicture({
          file: URL.createObjectURL(files[0]),
          name: files[0].name,
        });
        setSelectedPictureDimensions(
          await imageSize(URL.createObjectURL(files[0]))
        );
      } else {
        toast(warning(t("cloud.incorrectMimeType")));
      }
    }
  };

  const onFileChange = async (e) => {
    const { files } = e.target;

    if (files.length > 0) {
      if (hasRightMime(files[0])) {
        setSelectedPicture({
          file: URL.createObjectURL(files[0]),
          name: files[0].name,
        });
        setSelectedPictureDimensions(
          await imageSize(URL.createObjectURL(files[0]))
        );
      } else {
        toast(warning(t("cloud.incorrectMimeType")));
      }
    }

    fileInput.current.value = "";
  };

  return (
    <>
      <input
        hidden
        type="file"
        name="file-upload"
        id="file-upload"
        ref={fileInput}
        accept="image/jpeg,image/png"
        onChange={onFileChange}
      ></input>

      <Modal
        motionPreset="slideInBottom"
        isOpen
        onClose={onClose}
        preserveScrollBarGap
        size="6xl"
        isCentered
      >
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>{t("upload")}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {!selectedPicture ? (
                <FormControl>
                  <Flex
                    justify="center"
                    px={6}
                    pt={5}
                    pb={6}
                    borderWidth={2}
                    borderColor="light.300"
                    borderStyle="dashed"
                    rounded="md"
                    minH="300px"
                    align="center"
                    onDrop={onFileDrop}
                    onDragOver={onDrag}
                    onDragEnter={onDrag}
                    onDragLeave={onDrag}
                  >
                    <Stack spacing={1} textAlign="center">
                      <Icon
                        mx="auto"
                        boxSize={12}
                        color="text.500"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                        aria-hidden="true"
                      >
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </Icon>
                      <Flex
                        fontSize="sm"
                        color="text.500"
                        alignItems="baseline"
                      >
                        <Trans i18nKey="cloud.dragAndDrop">
                          <chakra.label
                            htmlFor="file-upload"
                            cursor="pointer"
                            rounded="md"
                            fontSize="md"
                            color="main.800"
                            pos="relative"
                            _hover={{
                              color: "main.900",
                            }}
                          >
                            <span>Wybierz plik</span>
                          </chakra.label>
                          <Text pl={1}>lub przeciągnij i upuść</Text>
                        </Trans>
                      </Flex>
                    </Stack>
                  </Flex>
                </FormControl>
              ) : (
                <>
                  <Flex
                    justifyContent="center"
                    display={{ base: "none", md: "flex" }}
                  >
                    <AvatarEdit
                      src={selectedPicture.file}
                      width={400}
                      height={400}
                      imageWidth={
                        selectedPictureDimensions?.width <
                        selectedPictureDimensions?.height
                          ? 400
                          : undefined
                      }
                      imageHeight={
                        selectedPictureDimensions?.height <
                        selectedPictureDimensions?.width
                          ? 400
                          : undefined
                      }
                      cropRadius={200}
                      minCropRadius={25}
                      exportAsSquare
                      exportSize={800}
                      onCrop={setCroppedPicture}
                      onClose={() => {
                        setSelectedPicture(null);
                        setSelectedPictureDimensions(null);
                        setCroppedPicture(null);
                      }}
                    />
                  </Flex>
                  <Flex
                    justifyContent="center"
                    display={{ base: "flex", md: "none" }}
                  >
                    <AvatarEdit
                      src={selectedPicture.file}
                      width={200}
                      height={200}
                      imageWidth={
                        selectedPictureDimensions?.width <
                        selectedPictureDimensions?.height
                          ? 200
                          : undefined
                      }
                      imageHeight={
                        selectedPictureDimensions?.height <
                        selectedPictureDimensions?.width
                          ? 200
                          : undefined
                      }
                      cropRadius={100}
                      minCropRadius={25}
                      exportAsSquare
                      exportSize={800}
                      onCrop={setCroppedPicture}
                      onClose={() => {
                        setSelectedPicture(null);
                        setSelectedPictureDimensions(null);
                        setCroppedPicture(null);
                      }}
                    />
                  </Flex>
                </>
              )}
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" onClick={onClose}>
                {t("cancel")}
              </Button>
              <Button
                onClick={uploadPicture}
                disabled={!croppedPicture}
                isLoading={isSubmitting}
                ml={3}
              >
                {t("save")}
              </Button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      </Modal>
    </>
  );
};

export default ChooseAvatarModal;
